/**
 * Per-pre-order Confirm/Export in-flight store.
 *
 * The recap action bar owns its confirm / AI / retry flags as React-local
 * state — none of which is visible to sibling components that need to lock
 * during the same window. Two scenarios this store exists to cover:
 *
 *   1. Between click and the server's SSE delivery of
 *      `pre_order_metadata.export.status = "in_progress"`, only the
 *      button itself knows a confirm is in flight. Header inputs
 *      (partner, dates, employee, …) on the focus page stay editable
 *      for that window even though the action is irreversible the
 *      moment the POST returns 202.
 *
 *   2. Partner-level "Accept AI" batch fan-out from the list view —
 *      mirrors the per-PO `aiInFlightStore` so per-PO sections lock
 *      during the batch.
 *
 * Read by `usePreOrderLock` so flows, comment block, and ModifyControls
 * inherit the lock without prop-drilling. Read by
 * `useIsPreOrderConfirming(preOrder)` from `PreOrderFocusView` so the
 * focus-page header (delete, client section, order details, ref button)
 * follows the same lock — combined with the server-side
 * `export.status === "in_progress"` signal so the long M3 round-trip
 * window is covered too.
 *
 * Pattern mirrors `aiInFlightStore` — same per-id subscription model,
 * same auto-drop of fully-idle slices to keep the map small.
 */

import { useSyncExternalStore } from "react";

export interface ConfirmInFlightSlice {
  isConfirming: boolean;
}

const EMPTY_SLICE: ConfirmInFlightSlice = Object.freeze({
  isConfirming: false,
});

type Listener = () => void;

class ConfirmInFlightStore {
  private readonly slices = new Map<string, ConfirmInFlightSlice>();
  private readonly listeners = new Set<Listener>();

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSlice = (preOrderId: string): ConfirmInFlightSlice => {
    return this.slices.get(preOrderId) ?? EMPTY_SLICE;
  };

  setConfirming(preOrderId: string, value: boolean): void {
    const current = this.slices.get(preOrderId) ?? EMPTY_SLICE;
    if (current.isConfirming === value) return;
    if (value) {
      this.slices.set(preOrderId, { isConfirming: true });
    } else {
      this.slices.delete(preOrderId);
    }
    for (const l of this.listeners) l();
  }
}

const confirmInFlightStore = new ConfirmInFlightStore();

/** Imperative API for non-React callers (`actions/transitions.ts`). */
export const confirmInFlight = {
  setConfirming: (preOrderId: string, value: boolean): void => {
    confirmInFlightStore.setConfirming(preOrderId, value);
  },
  getSlice: (preOrderId: string): ConfirmInFlightSlice => {
    return confirmInFlightStore.getSlice(preOrderId);
  },
};

/**
 * Subscribe to the confirm in-flight slice for a single pre-order.
 * Re-renders only when *this* PO's slice changes.
 */
export function useConfirmInFlightSlice(
  preOrderId: string,
): ConfirmInFlightSlice {
  return useSyncExternalStore(confirmInFlightStore.subscribe, () =>
    confirmInFlightStore.getSlice(preOrderId),
  );
}

/**
 * Aggregates the local store and the server-side
 * `pre_order_metadata.export.status === "in_progress"` flag into one
 * boolean. Used by `PreOrderFocusView` to lock header-level controls
 * (delete, partner, dates, employee, depot, route, address, ref) which
 * don't sit inside `PreOrderLockContext`. The full lock for flows /
 * comment / modify-controls is computed by `usePreOrderLock` directly.
 */
export function useIsPreOrderConfirming(preOrder: {
  id: string;
  pre_order_metadata?: { export?: { status?: string | null } | null } | null;
}): boolean {
  const { isConfirming } = useConfirmInFlightSlice(preOrder.id);
  const exportStatus = preOrder.pre_order_metadata?.export?.status ?? null;
  return isConfirming || exportStatus === "in_progress";
}
